import React from 'react';
import { Check } from 'lucide-react';

export interface WizardStep {
    id: string;
    label: string;
    description?: string;
}

interface DriverWizardStepperProps {
    steps: WizardStep[];
    currentStep: string;
    completedSteps: string[];
    onStepClick?: (stepId: string) => void;
    allowNavigation?: boolean;
}

const DriverWizardStepper: React.FC<DriverWizardStepperProps> = ({
    steps,
    currentStep,
    completedSteps,
    onStepClick,
    allowNavigation = false,
}) => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);

    const getStepStatus = (stepId: string, index: number): 'completed' | 'current' | 'upcoming' => {
        if (completedSteps.includes(stepId)) return 'completed';
        if (stepId === currentStep) return 'current';
        return 'upcoming';
    };

    const isClickable = (stepId: string, index: number): boolean => {
        if (!allowNavigation || !onStepClick) return false;
        // Allow clicking on completed steps or current step
        return completedSteps.includes(stepId) || stepId === currentStep;
    };

    return (
        <div className="w-full">
            {/* Desktop: Horizontal Stepper */}
            <div className="hidden md:block">
                <div className="flex items-center justify-between">
                    {steps.map((step, index) => {
                        const status = getStepStatus(step.id, index);
                        const clickable = isClickable(step.id, index);

                        return (
                            <React.Fragment key={step.id}>
                                {/* Step Circle */}
                                <div className="flex flex-col items-center flex-1">
                                    <button
                                        onClick={() => clickable && onStepClick?.(step.id)}
                                        disabled={!clickable}
                                        className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                      transition-all duration-200
                      ${status === 'completed' ? 'bg-success text-success-foreground' : ''}
                      ${status === 'current' ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : ''}
                      ${status === 'upcoming' ? 'bg-muted text-muted-foreground' : ''}
                      ${clickable ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
                    `}
                                    >
                                        {status === 'completed' ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span>{index + 1}</span>
                                        )}
                                    </button>

                                    {/* Step Label */}
                                    <div className="mt-2 text-center">
                                        <p
                                            className={`text-sm font-medium ${status === 'current' ? 'text-foreground' : 'text-muted-foreground'
                                                }`}
                                        >
                                            {step.label}
                                        </p>
                                        {step.description && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Connector Line */}
                                {index < steps.length - 1 && (
                                    <div className="flex-1 h-0.5 mx-4 mb-8">
                                        <div
                                            className={`h-full transition-colors duration-200 ${index < currentIndex ? 'bg-success' : 'bg-muted'
                                                }`}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Mobile: Vertical Stepper */}
            <div className="md:hidden space-y-4">
                {steps.map((step, index) => {
                    const status = getStepStatus(step.id, index);
                    const clickable = isClickable(step.id, index);

                    return (
                        <div key={step.id} className="flex items-start gap-4">
                            {/* Step Circle and Connector */}
                            <div className="flex flex-col items-center">
                                <button
                                    onClick={() => clickable && onStepClick?.(step.id)}
                                    disabled={!clickable}
                                    className={`
                    w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs
                    transition-all duration-200 flex-shrink-0
                    ${status === 'completed' ? 'bg-success text-success-foreground' : ''}
                    ${status === 'current' ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : ''}
                    ${status === 'upcoming' ? 'bg-muted text-muted-foreground' : ''}
                    ${clickable ? 'cursor-pointer' : 'cursor-default'}
                  `}
                                >
                                    {status === 'completed' ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        <span>{index + 1}</span>
                                    )}
                                </button>

                                {/* Connector Line */}
                                {index < steps.length - 1 && (
                                    <div className="w-0.5 h-12 my-1">
                                        <div
                                            className={`h-full transition-colors duration-200 ${index < currentIndex ? 'bg-success' : 'bg-muted'
                                                }`}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Step Content */}
                            <div className="flex-1 pb-4">
                                <p
                                    className={`text-sm font-medium ${status === 'current' ? 'text-foreground' : 'text-muted-foreground'
                                        }`}
                                >
                                    {step.label}
                                </p>
                                {step.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DriverWizardStepper;
